//#region Imports
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
//#endregion

//#region Internal Imports
import { HttpProvider } from 'app/core/common/http-provider.service';
import {
  CustomField,
  CustomFieldGroup,
  CustomFieldOption,
  CustomFieldSchema,
  ModuleAssignment,
  ModuleScope,
} from 'app/core/model/custom-fields/_module';
//#endregion

@Injectable({
  providedIn: 'root',
})
export class CustomFieldsService extends HttpProvider {
  //#region Endpoints
  private readonly groupsEndpoint = '/custom-fields/groups/';
  private readonly fieldsEndpoint = '/custom-fields/fields/';
  private readonly assignmentsEndpoint = '/custom-fields/assignments/';
  private readonly assignmentsReorderEndpoint = '/custom-fields/assignments/reorder/';
  private readonly conditionsEndpoint = '/custom-fields/conditions/';
  private readonly schemaEndpoint = '/custom-fields/schema/';
  private readonly valuesEndpoint = '/custom-fields/values/';
  //#endregion

  constructor(private httpClient: HttpClient) {
    super(httpClient);
  }

  // ---------------------------------------------------------------------------
  // Field Groups
  // ---------------------------------------------------------------------------

  getGroups(): Observable<CustomFieldGroup[]> {
    return this.get(this.groupsEndpoint, true).pipe(map((r) => r.data));
  }

  createGroup(data: Partial<CustomFieldGroup>): Observable<CustomFieldGroup> {
    return this.post(this.groupsEndpoint, data, true).pipe(map((r) => r.data));
  }

  updateGroup(id: number, data: Partial<CustomFieldGroup>): Observable<CustomFieldGroup> {
    return this.put(this.groupsEndpoint + `${id}/`, data, true).pipe(map((r) => r.data));
  }

  deleteGroup(id: number): Observable<any> {
    return this.delete(this.groupsEndpoint + `${id}/`);
  }

  // ---------------------------------------------------------------------------
  // Custom Fields
  // ---------------------------------------------------------------------------

  getFields(module?: ModuleScope): Observable<CustomField[]> {
    const options = module ? this.toHttpParams({ module }) : undefined;
    return this.get(this.fieldsEndpoint, true, options).pipe(map((r) => r.data));
  }

  createField(data: Partial<CustomField>): Observable<CustomField> {
    return this.post(this.fieldsEndpoint, data, true).pipe(map((r) => r.data));
  }

  updateField(id: number, data: Partial<CustomField>): Observable<CustomField> {
    return this.put(this.fieldsEndpoint + `${id}/`, data, true).pipe(map((r) => r.data));
  }

  deleteField(id: number): Observable<any> {
    return this.delete(this.fieldsEndpoint + `${id}/`);
  }

  // ---------------------------------------------------------------------------
  // Dropdown / Multi-select Options
  // ---------------------------------------------------------------------------

  getOptions(fieldId: number): Observable<CustomFieldOption[]> {
    return this.get(this.fieldsEndpoint + `${fieldId}/options/`, true).pipe(map((r) => r.data));
  }

  createOption(fieldId: number, data: Partial<CustomFieldOption>): Observable<CustomFieldOption> {
    return this.post(this.fieldsEndpoint + `${fieldId}/options/`, data, true).pipe(map((r) => r.data));
  }

  updateOption(fieldId: number, optionId: number, data: Partial<CustomFieldOption>): Observable<CustomFieldOption> {
    return this.put(this.fieldsEndpoint + `${fieldId}/options/${optionId}/`, data, true).pipe(map((r) => r.data));
  }

  deleteOption(fieldId: number, optionId: number): Observable<any> {
    return this.delete(this.fieldsEndpoint + `${fieldId}/options/${optionId}/`);
  }

  // ---------------------------------------------------------------------------
  // Module Assignments
  // ---------------------------------------------------------------------------

  getAssignments(module?: ModuleScope): Observable<ModuleAssignment[]> {
    const options = module ? this.toHttpParams({ module }) : undefined;
    return this.get(this.assignmentsEndpoint, true, options).pipe(map((r) => r.data));
  }

  createAssignment(data: Partial<ModuleAssignment>): Observable<ModuleAssignment> {
    return this.post(this.assignmentsEndpoint, data, true).pipe(map((r) => r.data));
  }

  updateAssignment(id: number, data: Partial<ModuleAssignment>): Observable<ModuleAssignment> {
    return this.put(this.assignmentsEndpoint + `${id}/`, data, true).pipe(map((r) => r.data));
  }

  deleteAssignment(id: number): Observable<any> {
    return this.delete(this.assignmentsEndpoint + `${id}/`);
  }

  reorderAssignments(items: { id: number; order: number }[]): Observable<any> {
    return this.put(this.assignmentsReorderEndpoint, items, true);
  }

  // ---------------------------------------------------------------------------
  // Conditions (admin only)
  // ---------------------------------------------------------------------------

  getConditions(): Observable<any[]> {
    return this.get(this.conditionsEndpoint, true).pipe(map((r) => r.data));
  }

  createCondition(data: { trigger_field: number; trigger_value: string; target_field: number; action: 'show' | 'hide' }): Observable<any> {
    return this.post(this.conditionsEndpoint, data, true).pipe(map((r) => r.data));
  }

  deleteCondition(id: number): Observable<any> {
    return this.delete(this.conditionsEndpoint + `${id}/`);
  }

  // ---------------------------------------------------------------------------
  // Schema & Values (all authenticated users)
  // ---------------------------------------------------------------------------

  getSchema(module: ModuleScope, recordId?: number): Observable<CustomFieldSchema> {
    const options = recordId ? this.toHttpParams({ record_id: recordId }) : undefined;
    return this.get(this.schemaEndpoint + `${module}/`, true, options).pipe(map((r) => r.data));
  }

  getValues(module: ModuleScope, recordId: number): Observable<Record<string, any>> {
    return this.get(this.valuesEndpoint + `${module}/${recordId}/`, true).pipe(map((r) => r.data));
  }

  saveValues(module: ModuleScope, recordId: number, values: Record<string, any>): Observable<Record<string, any>> {
    return this.post(this.valuesEndpoint + `${module}/${recordId}/`, values, true).pipe(map((r) => r.data));
  }
}
